import { Table, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { AutoCompleteSelect } from '@components/core-ui/form-elements/AutocompleteSelect';
import { BoldButton } from '@components/core-ui/form-elements/buttons/Button/variations/BoldButton';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import SearchIcon from '@components/icons/Search.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { ModalFooterEnd } from '@modals/core-ui/ModalFooter/variations/ModalFooterEnd';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { useAuthContext } from '@providers/AuthProvider';
import { useAPI } from '@providers/useAPI';
import { useQuery } from '@tanstack/react-query';
import { teamsKeys } from 'app/queries/teams/teamsKeys';
import { useState } from 'react';

import { createFetchMemberTeamQueryFn } from './api/fetchMemberTeam';
import { createFetchTeamAvailableMembersQueryFn } from './api/fetchTeamAvailableMembers';
import { canRemoveTeamMember } from './helpers/canRemoveTeamMember';
import { deriveMemberEditorState } from './helpers/deriveMemberEditorState';
import { diffTeamMemberIds } from './helpers/diffTeamMemberIds';
import { pickMemberships } from './helpers/pickMemberships';
import { withoutMemberId } from './helpers/withoutMemberId';
import { Content } from './styles/Content';
import { TProps } from './types/TProps';

export const EditTeamMembersModal = ({ teamId, onCancel, onSuccess }: TProps) => {
  const { call } = useAPI();
  const { me } = useAuthContext();

  const [draftMemberIds, setDraftMemberIds] = useState<ReadonlySet<string> | null>(null);

  const teamQuery = useQuery({
    queryKey: teamsKeys.detail(teamId),
    queryFn: createFetchMemberTeamQueryFn(call),
    gcTime: 0,
  });

  const availableMembersQuery = useQuery({
    queryKey: teamsKeys.availableMembersByTeam(teamId),
    queryFn: createFetchTeamAvailableMembersQueryFn(call),
    gcTime: 0,
  });

  const {
    members,
    available,
    draftMemberIds: draft,
    persistedMemberIds,
  } = deriveMemberEditorState({
    teamMembers: teamQuery.data?.Members ?? [],
    availableMembers: availableMembersQuery.data ?? [],
    draftMemberIds,
  });

  const addMember = (membershipId: string) =>
    setDraftMemberIds((current) => new Set(current ?? draft).add(membershipId));

  const removeMember = (membershipId: string) =>
    setDraftMemberIds((current) => withoutMemberId(current ?? draft, membershipId));

  const roster = [...(teamQuery.data?.Members ?? []), ...(availableMembersQuery.data ?? [])];

  const save = () => {
    const { added, removed } = diffTeamMemberIds({ persistedMemberIds, draftMemberIds: draft });

    onSuccess({ added: pickMemberships(roster, added), removed });
  };

  const reload = () => Promise.all([teamQuery.refetch(), availableMembersQuery.refetch()]);

  const isLoading = teamQuery.isLoading || availableMembersQuery.isLoading;
  const hasLoadError = teamQuery.isError || availableMembersQuery.isError;

  return (
    <ModalWrapper width="600px">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Edit Team Members</ModalTitle>
      <Content>
        <AutoCompleteSelect
          customOpenIcon={SearchIcon}
          customCloseIcon={SearchIcon}
          name="search"
          label="Search users"
          onChange={(_name, membershipId) => addMember(membershipId)}
          options={available.map((member) => ({
            label: `${member.fullName} ${member.email}`,
            value: member.membershipId,
          }))}
        />
        {availableMembersQuery.data?.length === 0 ? (
          <p className="text-sm text-foreground-muted">
            All members of this organization are already members of this team
          </p>
        ) : null}

        <div className="tableWrapper">
          <Table>
            <Thead $white>
              <Tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {members.map((member) => (
                <Tr key={member.membershipId}>
                  <Td>
                    <p className="max-w-[235px]">{member.fullName}</p>
                  </Td>
                  <Td>
                    <p className="max-w-[235px]">{member.email}</p>
                  </Td>
                  <Td>
                    {canRemoveTeamMember({
                      isDefaultTeam: teamQuery.data?.default ?? false,
                      memberUserId: member.userId,
                      currentUserId: me.id,
                      memberCount: members.length,
                    }) ? (
                      <IconButtonDelete title="Remove" onClick={() => removeMember(member.membershipId)}>
                        <TrashBin />
                      </IconButtonDelete>
                    ) : null}
                  </Td>
                </Tr>
              ))}
              {!isLoading && hasLoadError ? (
                <Tr className="no-border">
                  <Td colSpan={3} className="!max-w-none">
                    <div className="flex items-center justify-center gap-1">
                      <span className="text-sm">Unable to load the team members.</span>
                      <TextButton className="!px-0" onClick={() => void reload()}>
                        Try again
                      </TextButton>
                    </div>
                  </Td>
                </Tr>
              ) : null}
              {!isLoading && !hasLoadError && members.length === 0 ? (
                <Tr className="no-border">
                  <Td colSpan={3} className="!max-w-none text-center text-sm text-foreground-muted">
                    No team members yet.
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </div>

        <ModalFooterEnd>
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <BoldButton disabled={isLoading || hasLoadError} onClick={save}>
            Save
          </BoldButton>
        </ModalFooterEnd>
      </Content>
    </ModalWrapper>
  );
};
